import useDebounce from "app/hooks/useDebounce";
import { useCallback, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  changeReportSearch,
  setSearchQuery,
  toogleArchiveReportTableData,
} from "app/store/archiveReport/slice/archiveReportSlice";

import {
  useDownloadBulkReportsMutation,
  useDownloadReportListMutation,
} from "app/store/archiveReport/api/archiveReportApi";

import { addToDate, getFormattedDate, isDateInFuture } from "utils/date";
import { INVALID_DATE } from "static/date";
import momentTz from "moment-timezone";

import {
  CURRENT_DATE,
  EVOKE_ROLES,
  MOUNTAIN_TIME_ZONE,
} from "static/constants";
import { getCurrentUserRole } from "utils/currentUser.util";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { filterNonUCHAccounts } from "utils/account.util";

const useArchiveReportFilter = () => {
  const dispatch = useDispatch();
  const { page, pageSize, orderBy } = useSelector(
    (state) => state.archiveReport
  );

  const userRole = getCurrentUserRole();
  const { accounts } = useSelector((state) => state.account);
  const [downloadReportList, { isLoading: isDownloading }] =
    useDownloadReportListMutation();

  const [downloadBulkReports, { isLoading: isDownloadingBulkReports }] =
    useDownloadBulkReportsMutation();

  const regexPattern = /^[a-zA-Z0-9.,\s]*$/;

  const INITIAL_FILTER = {
    physicians: [],
    account: null,
    reportStartDate: null,
    reportEndDate: null,
    lastBillingStartDate: null,
    lastBillingEndDate: null,
    processedStartDate: null,
    processedEndDate: null,
    patient_name: "",
    patient_serial_num: "",
    billable: "",
    reportsOnEvoke: false,
    transmissionType: "",
  };

  const [filter, setFilter] = useState(INITIAL_FILTER);

  const debounceDispatch = useCallback(
    useDebounce((payload) => {
      dispatch(changeReportSearch(payload));
    }, 500),
    []
  );

  const filteredAccounts = filterNonUCHAccounts(accounts);

  useEffect(() => {
    if (filteredAccounts?.length === 1 && !EVOKE_ROLES.includes(userRole)) {
      onInputChange("account", filteredAccounts?.[0]?.name);
    }
  }, [accounts, userRole]);

  const onInputChange = (name, value) => {
    const payload = {
      ...filter,
      [name]: value,
    };

    if (
      name === "reportStartDate" &&
      (!filter.reportEndDate || filter.reportEndDate === INVALID_DATE)
    ) {
      const newEndDate = addToDate(value, 1, "years");
      payload["reportEndDate"] = isDateInFuture(newEndDate)
        ? getFormattedDate(CURRENT_DATE)
        : newEndDate;
    }

    if (name === "processedStartDate" && !filter.processedEndDate) {
      const newEndDate = addToDate(value, 365, "days");
      payload["processedEndDate"] = isDateInFuture(newEndDate)
        ? getFormattedDate(CURRENT_DATE)
        : newEndDate;
    }

    if (name === "lastBillingStartDate" && !filter.lastBillingEndDate) {
      const newEndDate = addToDate(value, 365, "days");
      payload["lastBillingEndDate"] = isDateInFuture(newEndDate)
        ? getFormattedDate(CURRENT_DATE)
        : newEndDate;
    }

    setFilter({
      ...payload,
    });

    debounceDispatch(payload);
  };

  const getSearchQuery = () => {
    return Object.entries(filter)
      ?.map(([key, value]) => {
        if (!value) return null;

        if (
          [
            "reportStartDate",
            "reportEndDate",
            "processedStartDate",
            "processedEndDate",
            "lastBillingStartDate",
            "lastBillingEndDate",
          ].includes(key)
        ) {
          const isEndDate = [
            "reportEndDate",
            "processedEndDate",
            "lastBillingEndDate",
          ].includes(key);

          const dateWithTime = isEndDate
            ? momentTz.tz(value, MOUNTAIN_TIME_ZONE).endOf("day")
            : momentTz.tz(value, MOUNTAIN_TIME_ZONE).startOf("day");
          const newDate = dateWithTime.utc().format("YYYY-MM-DDTHH:mm:ss");

          return `${key}=${newDate}&`;
        }

        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}&`;
      })
      ?.filter(Boolean)
      ?.join("");
  };

  const clearFilter = () => {
    setFilter(INITIAL_FILTER);
    debounceDispatch(INITIAL_FILTER);
    dispatch(setSearchQuery(""));
    dispatch(toogleArchiveReportTableData(false));
  };

  const onSubmit = () => {
    dispatch(setSearchQuery(getSearchQuery()));
    dispatch(toogleArchiveReportTableData(true));
  };

  const downloadReports = async (isBulkDownload = false) => {
    try {
      const searchQuery = getSearchQuery();

      const query = {
        page,
        pageSize,
        search: searchQuery,
        orderBy,
      };

      let result;
      if (isBulkDownload) {
        result = await downloadBulkReports(query);
      } else {
        result = await downloadReportList(query).unwrap();
      }

      if (result) {
        const successMessage = isBulkDownload
          ? "Archived reports downloaded successfully"
          : "Archive report list successfully downloaded.";
        showSuccessToast(successMessage);
      }
    } catch (error) {
      const messages = error?.message?.split(". ");
      messages.forEach((message) => showErrorToast(message));
    }
  };

  return {
    filter,
    regexPattern,
    isDownloading,
    isDownloadingBulkReports,
    onSubmit,
    onInputChange,
    downloadReports,
    clearFilter,
  };
};

export default useArchiveReportFilter;
